import { booleanAttribute, ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';

/**
 * Primary action button for side panel footers with a built-in saving state.
 * While `saving` is set the label stays in the DOM (hidden) and a small spinner is
 * overlaid on it, so the button keeps its size and alignment next to "Cancel".
 *
 * @example
 * <sim-save-button [saving]="saving()" [disabled]="!form.valid" (clicked)="saveChanges()">
 *   {{ saveLabel() }}
 * </sim-save-button>
 */
@Component({
  selector: 'sim-save-button',
  standalone: true,
  imports: [SimButtonModule, SimSpinnerComponent],
  template: `
    <button
      sim-flat-button
      class="sim-save-button__button"
      [class.sim-save-button__button--saving]="saving()"
      [simColor]="color()"
      [type]="type()"
      [disabled]="disabled() || saving()"
      (click)="clicked.emit()"
    >
      <span class="sim-save-button__label"><ng-content /></span>
      @if (saving()) {
        <span class="sim-save-button__spinner" aria-hidden="true">
          <sim-spinner [diameter]="20" [strokeWidth]="3" />
        </span>
      }
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
      .sim-save-button__button {
        position: relative;
      }
      .sim-save-button__button--saving .sim-save-button__label {
        visibility: hidden;
      }
      .sim-save-button__spinner {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sim-save-button' }
})
export class SimSaveButtonComponent {
  readonly saving = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly color = input<string>('primary');
  readonly type = input<'button' | 'submit'>('button');

  readonly clicked = output<void>();
}
